import { Modal } from 'bootstrap';
import PropTypes from 'prop-types';
import { CONF } from '../../../js/portal/environment';
import { getCurrentUserName } from '../../../js/portal/auth';
import {
  checkValueAgainstList,
  createPortalOrder,
  InfoTable,
  AccountInfoTable,
  showErrorFromApiOperation,
  showTemporaryRoleCommentModal,
  copyButtonJSXHelper,
  copyURLJSXHelper,
  loadOrderJSXHelper,
  hasAccess,
  displaySuccessPopup,
  showKillSwitchModal,
  showGrantLucidRoleModal,
  showUnlockS3OrSQSModal,
  showOptOutIdcPermissionSetModal,
  showReverseOptOutIdcPermissionSetModal,
  ssoStartUrls,
  getSsoFailoverUrl,
  SsoFailoverRegion,
} from '../../../js/portal/main';
import { baseApiAxios } from '../../../js/portal/api';
import {
  loadAccountM2MClientMappings,
  openAddM2MClientMappingsModal,
  openDeleteM2MClientMappingModal,
} from '../../../js/portal/accountClientMappings';
import { addSpinner, addButtonSpinner, removeSpinners } from '../../../js/portal/sidebar';
import { checkResponsibility } from '../../../js/portal/dashboard';
import { preEditAccount } from '../../../js/portal/accounts';
import { renderPolicyDetails } from '../../../js/portal/organizations';
import { enableFeatureFlagControlledElements } from '../../../js/portal/feature_flags';
// CLOUD-35639: Import order table setup and reusable datatable component for new Orders tab
import { addOrderData } from '../../../js/portal/orders';
import { Datatable } from '../../../Datatable';
import DatePickerField from './DatePickerField';

/**
 * Trigger an update of a given AWS account and add the loading animation to the sidebar and trigger button
 *
 * @param {string} buttonId the id of the button to show the loading animation
 * @param {string} accountId the target AWS account id which should be updated
 */
async function accountUpdateHandler(buttonId, accountId) {
  addSpinner();
  addButtonSpinner(buttonId);

  baseApiAxios
    .triggerAccountUpdate(accountId)
    .then(response => {
      displaySuccessPopup(response.message, 'Your request was successful.');
    })
    .catch(err => {
      showErrorFromApiOperation('Error trigger-update on account' + accountId)(err);
    })
    .finally(removeSpinners);
}

/**
 * Collect all currently assigned SCPs and RCPs for a given AWS account and show the loading animation of the load button
 *
 * @param {string} buttonId the id of the button to show the loading animation
 * @param {string} accountId the target AWS account id for which we want to collect all SCPs and RCPs
 * @param {object} sortedOrgUnits a sorted list of OUs from root up to the level where the account is located
 */
async function listPoliciesHandler(buttonId, accountId, sortedOrgUnits) {
  addSpinner();
  addButtonSpinner(buttonId);

  baseApiAxios
    .getRestrictions(accountId)
    .then(policyDetails => {
      renderPolicyDetails(policyDetails, accountId, sortedOrgUnits);
    })
    .catch(error => {
      showErrorFromApiOperation('Failed to load policies')(error);
    })
    .finally(removeSpinners);
}

// CLOUD-36101: Show the Export AWS Config Data modal and submit a real order on confirm
async function showExportConfigDataModal(accountId) {
  const modal = document.getElementById('sharedModal');
  $('#sharedModalTitle').html(<strong>Export AWS Config Data</strong>);
  $('#sharedModalContent').html(
    <div class="text-center py-3">
      <i class="fas fa-spinner fa-spin me-2"></i> Loading...
    </div>,
  );
  $('#sharedModalFooter').empty();
  Modal.getInstance(modal).show();

  // Fetch quota from the API before rendering the modal content
  let quotaMax = null;
  try {
    const meta = await baseApiAxios.getConfigExports(accountId);
    quotaMax = meta?.quota_max ?? null;
  } catch (_) {
    // non-fatal: we'll just omit the quota warning
  }

  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const twoYearsAgo = new Date(Date.now() - 730 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const Content = () => (
    <>
      <div class="alert alert-info px-3 py-2 mb-3">
        <i class="fas fa-info-circle me-2"></i>
        This will export AWS Config snapshot data for the selected date range as a CSV file. The maximum allowed range
        is 2 years (730 days). If the raw data is too large, the query may not be processable.
      </div>
      {quotaMax !== null && quotaMax > 0 && (
        <div class="alert alert-warning px-3 py-2 mb-3">
          <i class="fas fa-exclamation-triangle me-2"></i>
          There is a limit of <strong>{quotaMax} requests per account per month</strong>. If exceeded, the order will be
          rejected.
        </div>
      )}
      <form id="config-export-form" name="config-export-form" class="needs-validation" method="POST">
        <div class="form-group row mb-3">
          <label class="col-md-12 col-lg-3 col-xl-2 form-label-text text-align-center">
            <span class="text-truncate">Date From</span>
          </label>
          <div class="col-md-12 col-lg-9 col-xl-10">
            <DatePickerField
              id="config-export-start-date"
              selectedValue={thirtyDaysAgo}
              minValue={twoYearsAgo}
              maxValue={yesterday}
            />
          </div>
        </div>
        <div class="form-group row mb-3">
          <label class="col-md-12 col-lg-3 col-xl-2 form-label-text text-align-center">
            <span class="text-truncate">Date To</span>
          </label>
          <div class="col-md-12 col-lg-9 col-xl-10">
            <DatePickerField
              id="config-export-end-date"
              selectedValue={yesterday}
              minValue={twoYearsAgo}
              maxValue={yesterday}
            />
          </div>
        </div>
      </form>
    </>
  );

  const SubmitButton = () => (
    <button type="submit" class="btn btn-success" id="submit-config-export-order">
      <i class="fas fa-file-export me-1"></i> Submit Export Request
    </button>
  );

  $('#sharedModalContent').html(<Content />);
  $('#sharedModalFooter').append(<SubmitButton />);

  const handleSubmit = async () => {
    const startDate = document.getElementById('config-export-start-date').value;
    const endDate = document.getElementById('config-export-end-date').value;

    if (!startDate || !endDate) {
      displaySuccessPopup('Please select both a start and end date.', 'Missing Date Range');
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      displaySuccessPopup('Date From must be before or equal to Date To.', 'Invalid Date Range');
      return;
    }
    const diffDays = (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24);
    if (diffDays > 730) {
      displaySuccessPopup(
        'The selected date range exceeds the maximum allowed 2 years (730 days).',
        'Date Range Too Large',
      );
      return;
    }
    if (endDate >= new Date().toISOString().split('T')[0]) {
      displaySuccessPopup(
        "Date To cannot be today or in the future — today's snapshot has not been delivered yet.",
        'Invalid End Date',
      );
      return;
    }

    const submitBtn = document.getElementById('submit-config-export-order');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.innerHTML = '<i class="fas fa-spinner fa-spin me-1"></i> Submitting...';
    }

    try {
      const res = await baseApiAxios.post('/orders', {
        action: 'create-order',
        payload: {
          account_id: accountId,
          action: 'export-aws-config-report',
          date_from: startDate,
          description: `Export AWS Config Data for account ${accountId} from ${startDate} to ${endDate}`,
          date_to: endDate,
        },
      });
      Modal.getInstance(modal).hide();
      displaySuccessPopup(
        res?.message ||
          `Your Config Data export request for account ${accountId} (${startDate} to ${endDate}) has been submitted. Once completed, the CSV will be available in the Config Exports tab.`,
        'Order Submitted Successfully',
      );
    } catch (err) {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.innerHTML = '<i class="fas fa-file-export me-1"></i> Submit Export Request';
      }
      showErrorFromApiOperation('Error submitting config export order')(err);
    }
  };

  $('#submit-config-export-order').on('click', handleSubmit);
}

export function hasAdminAccountAccess(data) {
  let permissions = localStorage.permissions ? localStorage.permissions : false;
  let account_areas = localStorage.account_areas ? localStorage.account_areas : false;
  let responsible = checkResponsibility(data, getCurrentUserName());
  let area_admin = checkValueAgainstList(data['account_area'], account_areas);

  return (
    checkValueAgainstList('manage_organizations', permissions) ||
    checkValueAgainstList('manage_accounts', permissions) ||
    responsible ||
    area_admin
  );
}

// CLOUD-37764: who may see and use the M2M Client Mappings tab of an account. Mirrors the backend
// (AccessControlService.has_account_control) and the same access as hasAdminAccountAccess():
//  - manage_accounts permission (master admin), or
//  - manage_organizations permission, or
//  - a responsible of the account: primary, secondary, it_responsible or ops_responsibles (checkResponsibility), or
//  - an admin of the account's area.
export function hasM2MClientMappingsAccess(data) {
  const permissions = localStorage.permissions ? localStorage.permissions : false;
  const account_areas = localStorage.account_areas ? localStorage.account_areas : false;

  return !!(
    checkValueAgainstList('manage_accounts', permissions) ||
    checkValueAgainstList('manage_organizations', permissions) ||
    checkResponsibility(data, getCurrentUserName()) ||
    checkValueAgainstList(data['account_area'], account_areas)
  );
}

// Only a responsible or user with mangage_accounts permission can execute temporary root actions
export function hasExecuteTemporaryRootActionPermission(account) {
  let permissions = localStorage.permissions ? localStorage.permissions : false;
  let responsible = checkResponsibility(account, getCurrentUserName());

  return checkValueAgainstList('manage_accounts', permissions) || responsible;
}

export function hasManageLucidPermission() {
  let permissions = localStorage.permissions ? localStorage.permissions : false;
  return checkValueAgainstList('manage_lucid', permissions);
}

export const defaultIDCRoles = ['UserRO', 'UserFull', 'BMWBillingRO', 'BMWSecurityRO'];
// Account Access Manager is not available in the China partition
const AAM_PROFILE_REGION = 'eu-central-1';

function hasManageQuarantinePermission() {
  let permissions = localStorage.permissions ? localStorage.permissions : false;

  return checkValueAgainstList('manage_quarantine', permissions);
}

/**
 * Sort the given array of OUs by their parents. The root OU will be on the top, the target account OU on the bottom
 *
 * @param {string} orgOuId the current OU id of the target AWS account
 * @param {object} orgUnits an array with all OU ids which are parents of the orgOuId (one OU can have one parent OU)
 * @returns
 */
function sortParents(orgOuId, orgUnits) {
  // Create a new object out of the list of org units. The map will look like this:
  // {
  //   'r-z4vv': { org_out_id: 'r-z4vv', org_ou_name: 'Root' },
  //   'ou-z4vv-4idk2bbb': {
  //     org_out_id: 'ou-z4vv-4idk2bbb',
  //     org_ou_name: 'FPC-Administration',
  //     org_ou_parent_id: 'r-z4vv',
  //   },
  //   'ou-z4vv-kb5t5rc9': {
  //     org_out_id: 'ou-z4vv-kb5t5rc9',
  //     org_ou_name: 'Operations',
  //     org_ou_parent_id: 'ou-z4vv-4idk2bbb',
  //   },
  // }
  const temp = orgUnits.reduce((ouMap, ou) => {
    ouMap[ou.org_ou_id] = ou;
    return ouMap;
  }, {});
  let result = [];

  // Now we can scan through the new temporary object from above via while loop and always take the org_ou_parent_id
  // to go one level higher from the given OrgOuId up to the root OU in the tree
  let currentOu = orgOuId;
  while (temp[currentOu]) {
    result.push(temp[currentOu]);
    currentOu = temp[currentOu]?.org_ou_parent_id;
  }

  // Return the results in the reverse order, as we want to show the root OU on the top and the target OU on the bottom
  return result.reverse();
}

/**
 * Render the OU structure to visualize the hierarchy of the AWS account and its parent OUs
 *
 * @param {object} orgUnits an array with all OU ids which are parents of the orgOuId (one OU can have one parent OU)
 * @param {object} data the actual account item from the `account-information` table
 * @returns
 */
function renderTreeView(orgUnits, data) {
  // Create the outer container which contains the rendered OU structure
  const wrapper = document.createElement('div');
  wrapper.classList = 'pt-3';

  // Add all parent OUs of the account to the tree
  let currentOu = wrapper;
  orgUnits.forEach(ou => {
    const ouText = `${ou.org_ou_name} (${ou.org_ou_id})`;
    const li = (
      <li>
        <span class="fa-li">
          <i class="fa fa-angle-right"></i>
        </span>
        {ouText}
      </li>
    );
    const ul = <ul class="fa-ul">{li}</ul>;
    currentOu.appendChild(ul);
    currentOu = li;
  });

  // Add the actual AWS account itself to the tree
  const friendlyName = data.account_friendly_name ?? '';
  const newFriendlyName = friendlyName.length > 33 ? friendlyName.substring(0, 30).trim() + '...' : friendlyName;
  const ouText = `${newFriendlyName} (${data.account_id})`;
  const li = (
    <li>
      <span class="fa-li">
        <i class="fa fa-angle-right"></i>
      </span>
      <strong class="text-truncate">{ouText}</strong>
    </li>
  );
  const ul = <ul class="fa-ul">{li}</ul>;
  currentOu.appendChild(ul);

  // Return the fully rendered object
  return wrapper;
}

/**
 * This is the main content which represents the whole `AWS Account Details` content incl. all of the tabs.
 *
 * @param {object} data
 * @param {Array} tempAccessRoles
 * @param {boolean} isAccountDetailsPage
 * @param {object|null} aamLoginDetails Account Access Manager login details of the user for this account (null while the feature is hidden)
 * @param {boolean} aamLoading true while the Account Access Manager login details are being fetched
 * @returns {JSX.Element} the rendered account details incl. the tab content
 */
export default function AccountTabs({ data, tempAccessRoles, isAccountDetailsPage, aamLoginDetails, aamLoading }) {
  // use follow code as feature flag example
  // enable feature flag controlled elements
  enableFeatureFlagControlledElements('region.enable_regions', 'feature-flag-region-enable-regions');
  enableFeatureFlagControlledElements('region.disable_regions', 'feature-flag-region-disable-regions');
  enableFeatureFlagControlledElements('idc.enabled', 'feature-flag-idc-enabled');
  enableFeatureFlagControlledElements('idc.account_access_manager_enabled', 'feature-flag-aam-enabled');
  enableFeatureFlagControlledElements('temporary-privilege-escalation.enabled', 'temporary-privilege-escalation');
  enableFeatureFlagControlledElements('temporary-root-action.enabled', 'feature-flag-temporary-root-action');
  enableFeatureFlagControlledElements(
    'api-keys.m2m_client_account_mapping',
    'feature-flag-api-keys-m2m-client-account-mapping',
  );

  const showTempRoleModal = (button, role) => {
    showTemporaryRoleCommentModal(button, data, role, false);
  };

  // account details
  const editAccountFct = isAccountDetailsPage
    ? () => {
        preEditAccount(data);
      }
    : () => {
        window.location.href = `?account_id=${data.account_id}#accountdetails`;
      };

  const enableRegion = () => {
    window.location.href = `/region/enable-region?account_id=${data.account_id}`;
  };
  const disableRegion = () => {
    window.location.href = `/region/disable-region?account_id=${data.account_id}`;
  };

  const unquarantineAccount = async accountId => {
    // TODO: Do we need to add a reason and incident id for unquarantine since it is not used in the backend and
    // already exists in DB?
    const payload = {
      account_id: accountId,
      action: 'unquarantine-account',
      incident_id: 'placeholder incident id',
      description: `Unquarantine account ${accountId}`,
    };

    const requestBody = {
      action: 'create-order',
      payload,
    };
    addButtonSpinner('unquarantine-account-button');
    let unquarantineButton = document.getElementById('unquarantine-account-button');
    unquarantineButton.disabled = true;
    unquarantineButton.innerText = 'Submitting reactivation order...';
    const res = await baseApiAxios.post('/orders', requestBody);
    unquarantineButton.innerText = 'Reactivation order submitted.';
    //removeSpinners();
  };

  // CLOUD-35639: Lazy-load function for the Orders tab
  // Only loads the datatable when user clicks the Orders tab to improve page performance
  // Uses dataset.loaded flag to prevent loading the table multiple times if user switches tabs back and forth
  const accountOrdersTableId = 'account-orders-' + data.account_id;
  const accountOrdersContainerId = 'account-orders-container-' + data.account_id;

  const loadAccountOrdersTab = () => {
    const container = document.getElementById(accountOrdersContainerId);
    if (!container || container.dataset.loaded === 'true') return;
    container.dataset.loaded = 'true';
    // Insert the datatable component into the tab container
    $(container).html(<Datatable tableId={accountOrdersTableId} />);
    // Load orders filtered by this account's ID with Account Id column hidden
    addOrderData(accountOrdersTableId, null, null, null, { accountId: data.account_id, hideAccountIdColumn: true });
  };

  // CLOUD-36099: Lazy-load function for the Config Exports tab
  // Fetches real data from GET /accounts/{id}/config-exports on first tab click.
  const configExportsContainerId = 'config-exports-container-' + data.account_id;

  const loadConfigExportsTab = (force = false) => {
    const container = document.getElementById(configExportsContainerId);
    if (!container) return;
    if (!force && container.dataset.loaded === 'true') return;
    container.dataset.loaded = 'true';

    $(container).html(
      <div class="text-center py-4">
        <i class="fas fa-spinner fa-spin me-2"></i> Loading export history...
      </div>,
    );

    baseApiAxios
      .getConfigExports(data.account_id)
      .then(result => {
        const items = result?.items || [];
        if (items.length === 0) {
          $(container).html(
            <div class="alert alert-secondary">
              <i class="fas fa-info-circle me-2"></i>
              No completed config exports found for this account yet.
            </div>,
          );
          return;
        }

        const rows = items.map(item => {
          const formatDate = iso => {
            if (!iso) return '—';
            const d = new Date(iso + 'T00:00:00');
            return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
          };
          const dateRange =
            item.date_from && item.date_to ? `${formatDate(item.date_from)} — ${formatDate(item.date_to)}` : '—';
          const dateKey = item.date_from && item.date_to ? `${item.date_from}_${item.date_to}` : null;
          const orderDate = item.creation_date ? item.creation_date.split('T')[0] : '—';
          const statusBadge =
            item.executed === 'SUCCESS' || item.executed === 'SUCCESS_NO_ACTIONS' ? (
              <span class="badge bg-success fs-6 px-3 py-2">Completed</span>
            ) : item.executed === 'FAILED' ? (
              <span class="badge bg-danger fs-6 px-3 py-2">Failed</span>
            ) : (
              <span class="badge bg-warning text-dark fs-6 px-3 py-2">Processing</span>
            );
          const downloadCell = dateKey ? (
            <button
              class="btn-sm space text-nowrap"
              onclick={async e => {
                const btn = e.currentTarget;
                btn.disabled = true;
                btn.innerHTML = '<i class="fas fa-spinner fa-spin"></i>';
                try {
                  const res = await baseApiAxios.getConfigExportDownloadUrl(data.account_id, dateKey);
                  window.open(res.url, '_blank', 'noopener');
                } catch (err) {
                  showErrorFromApiOperation('Error generating download link')(err);
                } finally {
                  btn.disabled = false;
                  btn.innerHTML = '<i class="fas fa-download"></i> Get Download Link';
                }
              }}>
              <i class="fas fa-download"></i> Get Download Link
            </button>
          ) : (
            <span class="text-muted">—</span>
          );
          return (
            <tr role="row" class="compressed">
              <td class="small-width">{dateRange}</td>
              <td class="small-width">{orderDate}</td>
              <td class="small-width">{statusBadge}</td>
              <td class="small-width">{downloadCell}</td>
            </tr>
          );
        });

        $(container).html(
          <table class="table table-hover dataTable row-border compressed">
            <thead>
              <tr>
                <th class="table table-hover dataTable row-border nowrap compressed">Date Range</th>
                <th class="table table-hover dataTable row-border nowrap compressed">Order Date</th>
                <th class="table table-hover dataTable row-border nowrap compressed">Status</th>
                <th class="table table-hover dataTable row-border nowrap compressed">Download</th>
              </tr>
            </thead>
            <tbody>{rows}</tbody>
          </table>,
        );
      })
      .catch(showErrorFromApiOperation('Error loading config export history'));
  };

  // alternate contacts
  const initialOrderIds = Object.keys(data.orders || {});
  const alternateContactsEnabled = !!data.alternate_contacts;

  const isAccountQuarantined = data.account_area === 'quarantine';
  const deletedAccount = data.fpc_status === 'deleted';
  const isAdministrationAccount = data.account_area === 'fpc-administration';

  if (
    data.alternate_contacts?.OPERATIONS?.EmailAddress?.split?.('@')?.[1] === 'list.bmw.com' &&
    hasAdminAccountAccess(data)
  ) {
    data.alternate_contacts.OPERATIONS.actions = 'View/Edit Distribution List Members';
    data.alternate_contacts.OPERATIONS.account_id = data.account_id;
    data.alternate_contacts.OPERATIONS.dl_type = 'operations_dl';
  }

  // cost data
  let costDataContent;
  let costDataStatus = data?.cost_data?.cost_data_status || data?.cost_data_status;
  if (data.cost_data && costDataStatus && (costDataStatus === 'VALID' || costDataStatus === 'APPROVED')) {
    data.cost_data['default'] = data['cost_data_default'];
    costDataContent = (
      <AccountInfoTable
        data={data.cost_data}
        rowInfos={[
          { Name: 'Business Service ID', Value: 'business_service_id' },
          { Name: 'Business Service Name', Value: 'business_service_name' },
          { Name: 'Application Service ID', Value: 'application_service_id' },
          { Name: 'Application Service Name', Value: 'application_service_name' },
          { Name: 'CMDB ID', Value: 'cmdb_id' },
          { Name: 'Cloudroom Stage', Value: 'cloudroom_stage' },
          { Name: 'Cost Center', Value: 'cost_center' },
          { Name: 'WBS', Value: 'wbs' },
          { Name: 'Managed By', Value: 'managed_by' },
          { Name: 'Consumed By', Value: 'consumed_by' },
          { Name: 'Secured By', Value: 'secured_by' },
          { Name: 'IT Responsible', Value: 'it_responsible', CurrentUser: getCurrentUserName().toLowerCase() },
          {
            Name: 'App. Service Managed by group',
            Value: 'ops_responsibles',
            CurrentUser: getCurrentUserName().toLowerCase(),
          },
          { Name: 'App. Service Criticality', Value: 'application_service_criticality' },
          { Name: 'Service Offering', Value: 'service_offering' },
          { Name: 'Default', Value: 'default', IsBoolean: true },
          { Name: 'Status', Value: 'cost_data_status' },
        ]}
      />
    );
  } else {
    costDataContent = (
      <strong>
        Cost data is not validated for this account or data is not valid. Please visit the Billing DB Portal.
        <p>
          The current status is:{' '}
          {data.cost_data && data.cost_data['cost_data_status'] ? data.cost_data['cost_data_status'] : 'INVALID'}
        </p>
      </strong>
    );
  }

  // CLI config
  const cliConfigEnabled = !!data.idc_user_roles;
  // Account Access Manager (AAM): the backend already applied the feature flags, membership, entitlement and IAM role checks
  const aamRoles = aamLoginDetails?.enabled ? (aamLoginDetails.roles ?? []) : [];
  const hasAam = aamRoles.length > 0;
  const cliTabEnabled = cliConfigEnabled || hasAam;
  // IDC sign-in stops working after the opt-out, so it is only replaced once an AAM login exists
  const idcSuperseded = !!data.idc_ps_opted_out && hasAam;
  // ...so for an opted-out account the IDC sign-in content waits for the AAM answer instead of flashing up first
  const idcPending = !!data.idc_ps_opted_out && !!aamLoading;
  const aamProfiles = aamRoles.map(role => `${role.iam_role_name}-${data.account_id}`);
  const hasFailover = CONF.awsLoginDetails.awsPartition !== 'aws-cn';

  const switchCliConfigRegion = (e, suffix) => {
    e.currentTarget.parentElement.querySelector('.active')?.classList.remove('active');
    e.currentTarget.classList.add('active');
    const container = e.currentTarget.closest('[name="cli-config-code"]');
    container.querySelectorAll('.sso-session-block').forEach(el => (el.style.display = 'none'));
    container.querySelector(`.sso-session-${suffix}`).style.display = '';
    container.querySelectorAll('.cli-profile-block').forEach(el => (el.style.display = 'none'));
    container.querySelector(`.cli-profile-${suffix}`).style.display = '';
  };

  const switchCliMethod = (e, method) => {
    e.currentTarget.parentElement.querySelector('.active')?.classList.remove('active');
    e.currentTarget.classList.add('active');
    const container = e.currentTarget.closest('[name="cli-config-code"]');
    container.querySelectorAll('.cli-method-block').forEach(el => (el.style.display = 'none'));
    container.querySelector(`.cli-method-${method}`).style.display = '';
  };

  const copyToClipboard = (e, text) => {
    const button = e.currentTarget;
    navigator.clipboard?.writeText(text).then(() => {
      button.textContent = 'Copied';
      setTimeout(() => (button.textContent = 'Copy'), 2000);
    });
  };

  const copyButton = text => (
    <button type="button" class="btn-sm space mb-1" onclick={e => copyToClipboard(e, text)}>
      Copy
    </button>
  );

  // Organizations and policies
  const orgOuId = data.org_ou_id;
  const orgUnits = data.org_parents ?? [];
  const sortedOrgUnits = sortParents(orgOuId, orgUnits);
  const ouTree = renderTreeView(sortedOrgUnits, data);

  const policiesEnabled = !(data.fpc_status === 'deleted');

  const temporarilyUnlockRootOrder = function (event) {
    createPortalOrder({
      action: 'temporarily-unlock-root-user',
      description: 'Temporarily Unlock Root User for AWS Account "' + data.account_id + '"',
      account_id: data.account_id,
      hours: event.srcElement.querySelector('input[name="hours"]').value.toString(),
    });
  };
  const temporarilyUnlockRootHoursChange = function (event) {
    let e = event.srcElement;
    if (e.value && (e.value < e.min || e.value > e.max)) {
      e.value = e.min;
    }
  };

  // temporary access
  const tempAccessEnabled = data.fpc_status !== 'deleted' && tempAccessRoles?.length;

  // CLOUD-37764: M2M Client Mappings tab. Only for responsibles (primary/secondary/it/ops), manage_accounts users and
  // area admins of the account (hasM2MClientMappingsAccess), and only on the Account Details page once the account
  // data is loaded (AccountDetailsPage first renders this component with an empty placeholder `data`).
  // Additionally hidden (d-none) until the api-keys.m2m_client_account_mapping feature flag is true.
  const m2mClientMappingsEnabled = !!isAccountDetailsPage && !!data.account_id && hasM2MClientMappingsAccess(data);
  const m2mClientMappingsContainerId = 'm2m-client-mappings-container-' + data.account_id;

  // Lazy-loads the mappings of this account on first tab click; a failed load is retried on the next click.
  const loadM2MClientMappingsTab = (force = false) => {
    const container = document.getElementById(m2mClientMappingsContainerId);
    if (!container) return;
    if (!force && container.dataset.loaded === 'true') return;
    container.dataset.loaded = 'true';
    loadAccountM2MClientMappings(container, data.account_id, {
      onDelete: mapping =>
        openDeleteM2MClientMappingModal(data.account_id, mapping, { onSuccess: () => loadM2MClientMappingsTab(true) }),
    })
      .then(loaded => {
        if (!loaded) delete container.dataset.loaded;
      })
      .catch(err => {
        // Unexpected rendering error: allow a retry on the next click instead of leaving an unhandled rejection.
        delete container.dataset.loaded;
        console.error('Error rendering the M2M client mappings', err);
      });
  };

  // shared - login details
  const idcDefaultRoles = [
    'UserFull',
    'UserRO',
    'BMWSecurityRO',
    'BMWBillingRO',
    'BMWPurchasing',
    'BMWSupportRO',
    'BMWSupportAdmin',
  ];
  const idcSpecialRoles = ['BMWBillingMaster', 'BillingRO'];
  const bmwMasterAccounts = [
    '216268842436',
    '708820737385',
    '740783461026',
    '946848987374',
    '956879067252',
    '956774802373',
  ];

  const buildIdcRoleUrl = (role, accountId) => {
    const startUrl = ssoStartUrls[data.master_account];
    if (!startUrl) return '#';
    let roleSuffix;
    if (bmwMasterAccounts.includes(accountId) && (idcDefaultRoles.includes(role) || idcSpecialRoles.includes(role))) {
      roleSuffix = `mgmt-${role}`;
    } else if (idcDefaultRoles.includes(role)) {
      roleSuffix = role;
    } else {
      // Custom roles need async resolution — the href will be updated by accounts.js / accounts_table.js
      return startUrl;
    }
    return `${startUrl}/#/console?account_id=${accountId}&role_name=${roleSuffix}`;
  };

  const append_switch_idc_role_link = role => (
    <li>
      {role}:{' '}
      <a
        className={isAccountQuarantined ? 'disabled-link' : ''}
        id={role}
        href={buildIdcRoleUrl(role, data.account_id)}
        target="_blank"
        rel="noopener noreferrer">
        Switch Role
      </a>
    </li>
  );

  const aam_login_card = () => (
    <div class="card mb-3 border-primary" name="aam-login">
      <div class="card-body">
        <h5 class="card-title mb-2">Account Access Manager</h5>
        <p class="card-text mb-2">
          Sign in to the Account Access Manager Portal to access your Account Access Manager roles in the account.
        </p>
        <a
          class="btn btn-sm btn-primary mb-3"
          href={aamLoginDetails.login_url}
          target="_blank"
          rel="noopener noreferrer">
          Open login portal
        </a>
        <ul class="list-unstyled mb-0">
          {aamRoles.map(role => (
            <li class="d-flex justify-content-between align-items-center border-top py-1">
              <strong>{role.role_name}</strong>
              <a href={role.login_url} target="_blank" rel="noopener noreferrer">
                Switch Role
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  const login_details = (isAccountQuarantined = false) =>
    (data.cdh_user_roles || data.idc_user_roles || hasAam || aamLoading) && (
      <div>
        <h4>Login Details</h4>
        <p>
          You have access to: <strong>{Number(data.cdh_user_roles?.length) || '0'} CDH IAM role(s), </strong>
          <strong>{Number(data.idc_user_roles?.length) || '0'} IDC role(s) </strong>
          {hasAam && <strong>and {aamRoles.length} Account Access Manager role(s) </strong>}
          in this AWS account.
        </p>
        {aamLoading && (
          <div class="d-flex align-items-center mb-3" name="aam-login-loading">
            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            <span>Fetching Account Access Manager login details...</span>
          </div>
        )}
        {!isAccountQuarantined && hasAam && aam_login_card()}
        {!isAccountQuarantined && data.cdh_user_roles?.length && (
          <div>
            <p>
              Please{' '}
              <a href={CONF.awsLoginDetails.cdhLoginUrl} target="_blank" rel="noopener noreferrer">
                login to the CDH Portal
              </a>{' '}
              in order to access the following roles:
            </p>
            <ul>
              {data.cdh_user_roles.map(role => (
                <li>{role}</li>
              ))}
            </ul>
          </div>
        )}
        {!isAccountQuarantined && !idcPending && data.idc_user_roles?.length && (
          <div class={'card mb-3' + (idcSuperseded ? ' opacity-75' : '')} name="idc-login">
            <div class="card-body">
              <h5 class="card-title mb-2">AWS IAM Identity Center</h5>
              {idcSuperseded ? (
                <p class="card-text mb-0" name="idc-opted-out">
                  This account has opted out of IDC Permission Sets. Please sign in with Account Access Manager.
                </p>
              ) : (
                <>
                  <p class="mb-0">
                    Please{' '}
                    <a
                      href={ssoStartUrls[data.master_account]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={isAccountQuarantined ? 'disabled-link' : ''}>
                      login to AWS SSO Portal
                    </a>{' '}
                    to access identity center roles in the account.
                  </p>
                  <ul>{data.idc_user_roles.map(append_switch_idc_role_link)}</ul>
                </>
              )}
            </div>
          </div>
        )}
        {!isAccountQuarantined && (data.idc_user_roles?.length || hasAam) && (
          <div>
            <p>
              Please order access to this account via{' '}
              <a
                href="https://portal.rightnow.azure.cloud.bmw/"
                target="_blank"
                rel="noopener noreferrer"
                className={isAccountQuarantined ? 'disabled-link' : ''}>
                RightNow
              </a>{' '}
              and search for <code>{data.account_id}</code> in the category:{' '}
              <code>Base Technology & Infrastructure -&gt; AWS FPC -&gt; FPC Standard Cloudroom Roles</code>
            </p>
            <p>
              The Right Now documentation is available here:{' '}
              <a
                href="http://rightnow-info.bmwgroup.net"
                target="_blank"
                rel="noopener noreferrer"
                className={isAccountQuarantined ? 'disabled-link' : ''}>
                http://rightnow-info.bmwgroup.net
              </a>
            </p>
          </div>
        )}
        <p class="mt-3">
          More information about configuring the AWS CLI can be found in the{' '}
          <a
            href="https://developer-home.bmwgroup.net/docs/public-cloud-platform-aws/2_developyourapplication/gettingstarted/cli#aws-cli"
            target="_blank"
            rel="noopener noreferrer">
            Developer Home Docs
          </a>
          .
        </p>
        {hasFailover && !idcSuperseded && !idcPending && (
          <div>
            <p class="mb-0">
              {' '}
              Only if you are <strong>unable</strong> to log in with the first AWS SSO Portal link provided, please try
              one of the following failover URLs:
            </p>
            <ul>
              <li>
                <a
                  href={getSsoFailoverUrl(data.master_account, 'eu-west-1')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={isAccountQuarantined ? 'disabled-link' : ''}>
                  Failover eu-west-1
                </a>
              </li>
              <li>
                <a
                  href={getSsoFailoverUrl(data.master_account, 'us-east-1')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={isAccountQuarantined ? 'disabled-link' : ''}>
                  Failover us-east-1
                </a>
              </li>
            </ul>
          </div>
        )}
      </div>
    );

  const aam_cli_config = () => {
    const loginCommands = aamProfiles.map(profile => `aws login --profile ${profile}`).join('\n');
    const verifyCommand = `aws sts get-caller-identity --profile ${aamProfiles[0]}`;
    return (
      <div>
        <h4>AWS CLI / SDK with Account Access Manager</h4>
        <p>
          Sign in once in the browser, then let the AWS CLI reuse that session with <code>aws login</code>. This needs
          AWS CLI <strong>2.32.0</strong> or later. <code>aws sso login</code> does not work for Account Access Manager
          roles.
        </p>
        <ol>
          <li>
            <strong>Sign in with the role in your browser.</strong> Use the sign-in link of the role in the Login
            Details, or open the{' '}
            <a href={aamLoginDetails.login_url} target="_blank" rel="noopener noreferrer">
              login portal
            </a>{' '}
            and pick the account and role.
          </li>
          <li>
            <strong>Start a CLI session for that role.</strong> Use one profile per role so the CLI keeps the sessions
            apart:
            <div>{copyButton(loginCommands)}</div>
            <pre class="add-line-numbers" name="aam-login-commands">
              {aamProfiles.map(profile => (
                <span>aws login --profile {profile}</span>
              ))}
            </pre>
          </li>
          <li>
            <strong>Check the session.</strong>
            <div>{copyButton(verifyCommand)}</div>
            <pre class="add-line-numbers">
              <span>{verifyCommand}</span>
            </pre>
          </li>
        </ol>
        <p>
          <code>aws login</code> adds the profile to <code>~/.aws/config</code> (Windows:{' '}
          <code>%userprofile%\.aws\config</code>) for you:
        </p>
        <pre class="add-line-numbers" name="aam-profile-config">
          {aamProfiles.map(profile => (
            <>
              <span>[profile {profile}]</span>
              <span>login_session = &lt;set by aws login&gt;</span>
              <span>region = {AAM_PROFILE_REGION}</span>
            </>
          ))}
        </pre>
        <p>
          The session is refreshed automatically until it expires, then run <code>aws login</code> again. For tools that
          do not support <code>aws login</code> yet, choose <strong>Generate access key</strong> next to the role in the
          login portal and copy the temporary credentials.
        </p>
      </div>
    );
  };

  // support for feature flag
  let rowInfos = [
    { Name: 'Account Id', Value: 'account_id' },
    { Name: 'Root Account', Value: 'account_root_email' },
    { Name: 'Friendly Name', Value: 'account_friendly_name' },
    { Name: 'Description', Value: 'description' },
    { Name: 'FPC Status', Value: 'fpc_status' },
    { Name: 'Account Area', Value: 'account_area' },
    { Name: 'Account Type', Value: 'account_type' },
    { Name: 'Account Stage', Value: 'cost_data.cloudroom_stage' },
    { Name: 'AWS Support Plan', Value: 'support_plan' },
    { Name: 'IT Responsible', Value: 'it_responsible', CurrentUser: getCurrentUserName().toLowerCase() },
    {
      Name: 'App. Service Managed by group',
      Value: 'cost_data.ops_responsibles',
      CurrentUser: getCurrentUserName().toLowerCase(),
    },
    { Name: 'Creation Date', Value: 'creation_date' },
    { Name: 'Organizations Master Account', Value: 'master_account' },
    { Name: 'Organizations Id', Value: 'org_id' },
    { Name: 'Organizations Name', Value: 'org_name' },
    { Name: 'Organizations Unit Id', Value: 'org_ou_id' },
    { Name: 'Organizations Unit Name', Value: 'org_ou_name' },
    { Name: 'Organizations Account Status', Value: 'org_status' },
    { Name: 'Last Org Update (UTC)', Value: 'org_status_update' },
    { Name: 'App Service Criticality', Value: 'cost_data.application_service_criticality' },
    { Name: 'Service Offering', Value: 'cost_data.service_offering' },
  ];

  return (
    <div class="detailsContainer">
      <ul class="nav nav-tabs">
        <li class="nav-item active">
          <a
            class="nav-link active"
            data-bs-target={'#account' + data.account_id}
            role="tab"
            data-bs-toggle="tab"
            aria-selected="true"
            aria-expanded="true">
            Account
          </a>
        </li>
        <li class="nav-item">
          <a
            class={
              'nav-link' +
              (alternateContactsEnabled ? '' : ' disabled') +
              (isAccountQuarantined && alternateContactsEnabled ? ' disabled' : '')
            }
            data-bs-target={'#alternate_contacts' + data.account_id}
            role="tab"
            data-bs-toggle="tab"
            aria-selected="false"
            aria-expanded="false">
            Alternate Contacts
          </a>
        </li>
        <li class="nav-item">
          <a
            class={'nav-link' + (isAccountQuarantined ? ' disabled' : '')}
            data-bs-target={'#cost_data' + data.account_id}
            role="tab"
            data-bs-toggle="tab"
            aria-selected="false"
            aria-expanded="false">
            Cost Data
          </a>
        </li>
        <li class="nav-item">
          <a
            class={
              'nav-link' +
              (cliTabEnabled ? '' : ' disabled') +
              (isAccountQuarantined && cliTabEnabled ? ' disabled' : '')
            }
            data-bs-target={'#cli' + data.account_id}
            role="tab"
            data-bs-toggle="tab"
            aria-selected="false"
            aria-expanded="false">
            CLI Config
          </a>
        </li>
        {hasAccess(data.account_id, ['view_all_accounts', 'manage_accounts']) && (
          <li class="nav-item">
            <a
              class={'nav-link' + (isAccountQuarantined ? ' disabled' : '')}
              data-bs-target={'#org' + data.account_id}
              role="tab"
              data-bs-toggle="tab"
              aria-selected="false"
              aria-expanded="false">
              Organizations and Policies
            </a>
          </li>
        )}
        {/* CLOUD-35639: New Orders tab - positioned after Organizations and Policies
            When clicked, calls loadAccountOrdersTab() to on-demand load and render the orders table
            disabled when account is quarantined to prevent viewing/interacting with quarantined account orders
        */}
        <li class="nav-item">
          <a
            class={'nav-link' + (isAccountQuarantined ? ' disabled' : '')}
            data-bs-target={'#orders' + data.account_id}
            role="tab"
            data-bs-toggle="tab"
            aria-selected="false"
            aria-expanded="false"
            onclick={loadAccountOrdersTab}>
            Orders
          </a>
        </li>
        {/* CLOUD-36101: Config Exports tab - lazy-loads real data from the backend */}
        <li class="nav-item">
          <a
            class={'nav-link' + (isAccountQuarantined ? ' disabled' : '')}
            data-bs-target={'#config_exports' + data.account_id}
            role="tab"
            data-bs-toggle="tab"
            aria-selected="false"
            aria-expanded="false"
            onclick={loadConfigExportsTab}>
            Config Exports
          </a>
        </li>
        {/* CLOUD-37764: M2M Client Mappings tab, only rendered for users allowed to administer the account */}
        {m2mClientMappingsEnabled && (
          <li class="nav-item d-none feature-flag-api-keys-m2m-client-account-mapping">
            <a
              class={'nav-link' + (isAccountQuarantined ? ' disabled' : '')}
              href={'#m2m_client_mappings' + data.account_id}
              tabindex={isAccountQuarantined ? '-1' : '0'}
              data-bs-target={'#m2m_client_mappings' + data.account_id}
              role="tab"
              data-bs-toggle="tab"
              aria-selected="false"
              aria-expanded="false"
              onclick={() => loadM2MClientMappingsTab()}>
              M2M Client Mappings
            </a>
          </li>
        )}
        {tempAccessEnabled && (
          <li class="nav-item">
            <a
              class={'nav-link'}
              data-bs-target={'#access' + data.account_id}
              role="tab"
              data-bs-toggle="tab"
              aria-selected="false"
              aria-expanded="false">
              Temp. Access
            </a>
          </li>
        )}
      </ul>
      <div class="tab-content detailsTab">
        <div class="tab-pane fade active show" id={'account' + data.account_id} role="tabpanel">
          <div class="row" name="account-details-container">
            <div class="col-lg-12 col-xl-12" name="warnings-and-orders">
              {data.marked_for_deletion && data.fpc_status !== 'deleted' && (
                <div class="detailsContent">
                  <div style="color:black;">
                    <h3>
                      <strong>Warning:</strong> AWS Account is marked for deletion!
                    </h3>
                  </div>
                </div>
              )}
            </div>
            <div class="col-lg-12 col-xl-8" name="account-details-left-side">
              <div class="detailsContent">
                <h4>AWS Account Details</h4>
                {isAccountQuarantined && (
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <h5 class="alert alert-danger px-3 py-2 mb-0">
                      Info: Account is quarantined, no actions are possible.
                    </h5>
                    {!deletedAccount && hasManageQuarantinePermission() && (
                      <button
                        type="button"
                        class="btn btn-danger temporary-privilege-escalation"
                        onclick={e => {
                          unquarantineAccount(data.account_id);
                        }}
                        id="unquarantine-account-button"
                        style="height: 100%;">
                        <i class="fas fa-circle-exclamation"></i> Reactivate Account
                      </button>
                    )}
                  </div>
                )}
                <div>
                  <AccountInfoTable data={data} rowInfos={rowInfos} quarantined={isAccountQuarantined} />
                </div>
              </div>
              <div class="detailsContent">
                <div class="btn-group detail-actions-btn-group">
                  {hasAdminAccountAccess(data) && (
                    <>
                      <button
                        type="button"
                        class="btn-sm space"
                        id="trigger-account-update-button"
                        onclick={() => accountUpdateHandler('trigger-account-update-button', data.account_id)}
                        title="Collect account information like the alternate contacts or the support level">
                        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        <i class="fas fa-sync-alt" /> Trigger Account Update
                      </button>
                      <button
                        disabled={isAccountDetailsPage && isAccountQuarantined}
                        type="button"
                        class="btn-sm space"
                        onclick={editAccountFct}>
                        {isAccountDetailsPage ? (
                          <>
                            <i class="fas fa-pencil-alt" /> Edit Account
                          </>
                        ) : (
                          <>
                            <i class="fas fa-external-link-alt" /> Account Details
                          </>
                        )}
                      </button>
                      {!deletedAccount &&
                        !isAccountQuarantined &&
                        !isAdministrationAccount &&
                        hasManageQuarantinePermission() &&
                        data.account_id && (
                          <button
                            type="button"
                            class="space btn btn-small btn-danger temporary-privilege-escalation d-none"
                            id="quarantine-account-button"
                            onclick={() => showKillSwitchModal(data.account_id)}>
                            <i class="fas fa-circle-exclamation" /> Kill Switch
                          </button>
                        )}
                      <button
                        type="button"
                        disabled={isAccountQuarantined}
                        class="btn-sm space feature-flag-region-enable-regions d-none"
                        onclick={enableRegion}>
                        <i class="fas fa-external-link-alt" /> Enable Region
                      </button>
                      <button
                        type="button"
                        disabled={isAccountQuarantined}
                        class="btn-sm space feature-flag-region-disable-regions d-none"
                        onclick={disableRegion}>
                        <i class="fas fa-external-link-alt" /> Disable Region
                      </button>
                    </>
                  )}
                  {!deletedAccount && !isAccountQuarantined && hasManageLucidPermission() && data.account_id && (
                    <button
                      type="button"
                      class="btn-sm space"
                      id="grant-lucid-role-button"
                      onclick={() => showGrantLucidRoleModal(data.account_id)}>
                      <i class="fas fa-helmet-safety"></i> Enable Lucid Role
                    </button>
                  )}
                  {!deletedAccount &&
                    !isAccountQuarantined &&
                    checkResponsibility(data, getCurrentUserName()) &&
                    !data.idc_ps_opted_out &&
                    data.account_id && (
                      <div class="feature-flag-aam-enabled d-none">
                        <button
                          type="button"
                          class="btn-sm space"
                          id="opt-out-idc-permission-set-button"
                          onclick={() => showOptOutIdcPermissionSetModal(data.account_id)}>
                          <i class="fas fa-right-from-bracket"></i> Opt Out of IDC Permission Sets
                        </button>
                      </div>
                    )}
                  {!deletedAccount &&
                    !isAccountQuarantined &&
                    checkResponsibility(data, getCurrentUserName()) &&
                    data.idc_ps_opted_out &&
                    data.account_id && (
                      <div class="feature-flag-aam-enabled d-none">
                        <button
                          type="button"
                          class="btn-sm space"
                          id="reverse-opt-out-idc-permission-set-button"
                          onclick={() => showReverseOptOutIdcPermissionSetModal(data.account_id)}>
                          <i class="fas fa-right-to-bracket"></i> Reverse IDC Permission Sets Opt-Out
                        </button>
                      </div>
                    )}
                  {!deletedAccount &&
                    !isAccountQuarantined &&
                    hasExecuteTemporaryRootActionPermission(data) &&
                    data.account_id && (
                      <div class="feature-flag-temporary-root-action d-none">
                        <button
                          type="button"
                          class="btn-sm space"
                          id="unlock-s3-or-sqs-button"
                          onclick={() => showUnlockS3OrSQSModal(data.account_id)}>
                          <i class="fas fa-unlock-keyhole"></i> Unlock S3 or SQS
                        </button>
                      </div>
                    )}
                  {!deletedAccount && !isAccountQuarantined && data.account_id && (
                    <button
                      type="button"
                      class="btn-sm space"
                      id="export-config-data-button"
                      onclick={() => showExportConfigDataModal(data.account_id)}>
                      <i class="fas fa-file-export"></i> Export AWS Config Data
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div class="col-lg-12 col-xl-4" name="account-details-right-side">
              <div
                class={'detailsContent' + (isAccountQuarantined ? ' text-muted' : '')}
                name="account-details-switch-role-links">
                {login_details(isAccountQuarantined)}
              </div>
            </div>
          </div>
        </div>

        <div class="tab-pane fade" id={'alternate_contacts' + data.account_id} role="tabpanel">
          <div class="row" name="alternate-contacts-container">
            <div class="col-lg-12 col-xl-8">
              <div class="detailsContent table-fixed">
                <h4>Alternate Contacts</h4>
                <div name="alternate-contacts-billing">
                  {data.alternate_contacts?.BILLING && (
                    <div class="mt-4">
                      <h3 class="overview">BILLING</h3>
                      <div>
                        <InfoTable
                          data={data.alternate_contacts?.BILLING}
                          rowInfos={[
                            { Name: 'Contact', Value: 'Title' },
                            { Name: 'Phone Number', Value: 'PhoneNumber' },
                            { Name: 'Email', Value: 'EmailAddress' },
                            { Name: 'Actions', Value: 'actions' },
                          ]}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div name="alternate-contacts-operations">
                  {data.alternate_contacts?.OPERATIONS && (
                    <div class="mt-4">
                      <h3 class="overview">OPERATIONS</h3>
                      <div>
                        <InfoTable
                          data={data.alternate_contacts.OPERATIONS}
                          rowInfos={[
                            { Name: 'Contact', Value: 'Title' },
                            { Name: 'Phone Number', Value: 'PhoneNumber' },
                            { Name: 'Email', Value: 'EmailAddress' },
                            { Name: 'Actions', Value: 'actions' },
                          ]}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div name="alternate-contacts-security">
                  {data.alternate_contacts?.SECURITY && (
                    <div class="mt-4">
                      <h3 class="overview">SECURITY</h3>
                      <div>
                        <InfoTable
                          data={data.alternate_contacts.SECURITY}
                          rowInfos={[
                            { Name: 'Contact', Value: 'Title' },
                            { Name: 'Phone Number', Value: 'PhoneNumber' },
                            { Name: 'Email', Value: 'EmailAddress' },
                            { Name: 'Actions', Value: 'actions' },
                          ]}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="tab-pane fade" id={'cost_data' + data.account_id} role="tabpanel">
          <div class="row" name="cost-data-container">
            <div class="col-lg-12 col-xl-6">
              <div class="detailsContent">
                <h4>Cost Data Details</h4>
                {costDataContent}
              </div>
              {data.tags && Object.keys(data.tags).length > 0 && (
                <div class="detailsContent">
                  <h4>AWS Account Tags</h4>
                  <table class="table table-hover dataTable row-border compressed">
                    <thead>
                      <th class="table table-hover dataTable row-border nowrap compressed">Key</th>
                      <th class="table table-hover dataTable row-border nowrap compressed">Value</th>
                    </thead>
                    <tbody>
                      {Object.entries(data.tags)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([key, value]) => (
                          <tr key={key}>
                            <td>{key}</td>
                            <td>{value}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div class="detailsContent">
                <div id="infobox" class="card infobox">
                  <div class="card-body">
                    Visit the billing DB to <b>view</b> or <b>update</b> the cost data.
                    <br />
                    Data can be updated only from <b>IT responsible</b> or members of the <b>ops responsibles</b>.
                  </div>
                </div>
                <div class="btn-group detail-actions-btn-group">
                  <a
                    class="btn-sm space"
                    target="_blank"
                    style="text-decoration: none"
                    href={'https://cloudbilling.bmwgroup.net/user/mycosts?roomid=' + data.account_id}>
                    <i
                      class="fas fa-external-link-alt"
                      style="margin-right: 0.5em; font-size: smaller; align-self: center; margin-top: -0.1em"
                    />
                    View Costs{' '}
                  </a>
                  <a
                    class="btn-sm space"
                    target="_blank"
                    style="text-decoration: none"
                    href={'https://cloudbilling.bmwgroup.net/user/billingdata?roomid=' + data.account_id}>
                    <i
                      class="fas fa-external-link-alt"
                      style="margin-right: 0.5em; font-size: smaller; align-self: center; margin-top: -0.1em"
                    />
                    Update Cost Data{' '}
                  </a>
                  <a
                    class="btn-sm space"
                    target="_blank"
                    style="text-decoration: none"
                    href={'https://cloudbilling.bmwgroup.net/user/appdata?roomid=' + data.account_id}>
                    <i
                      class="fas fa-external-link-alt"
                      style="margin-right: 0.5em; font-size: smaller; align-self: center; margin-top: -0.1em"
                    />
                    Update Application Data{' '}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="tab-pane fade" id={'cli' + data.account_id} role="tabpanel">
          <div class="row" name="cli-config-container">
            <div class="col-lg-12 col-xl-4">
              <div class="detailsContent" name="cli-config-switch-role-links">
                {login_details(isAccountQuarantined)}
              </div>
            </div>

            <div class="col-lg-12 col-xl-8">
              <div class="detailsContent" name="cli-config-code">
                {hasAam && data.idc_user_roles && (
                  <div class="btn-group detail-actions-btn-group mb-3" role="group" aria-label="Login method">
                    <button type="button" class="btn-sm space active" onclick={e => switchCliMethod(e, 'aam')}>
                      Account Access Manager
                    </button>
                    <button type="button" class="btn-sm space" onclick={e => switchCliMethod(e, 'idc')}>
                      Identity Center
                    </button>
                  </div>
                )}
                {hasAam && <div class="cli-method-block cli-method-aam">{aam_cli_config()}</div>}
                {data.idc_user_roles && (
                  <div class="cli-method-block cli-method-idc" style={hasAam || idcPending ? 'display:none' : ''}>
                    <h4>AWS SSO Config</h4>
                    <p>
                      Here you can find a configuration example for your AWS command line based on this specific AWS
                      account and your available roles. By default your AWS CLI config file is located in your home
                      directory (Windows: <code>%userprofile%\.aws\config</code>, Linux and Mac:{' '}
                      <code>~/.aws/config</code>).
                    </p>
                    {hasFailover && (
                      <p>Use one of the failover configurations if the primary SSO start URL is not reachable.</p>
                    )}
                    {hasFailover && (
                      <div class="btn-group detail-actions-btn-group mb-3" role="group" aria-label="SSO session region">
                        <button
                          type="button"
                          class="btn-sm space active"
                          onclick={e => switchCliConfigRegion(e, 'primary')}>
                          Primary ({CONF.awsLoginDetails.loginRegion})
                        </button>
                        {Object.values(SsoFailoverRegion).map(region => (
                          <button type="button" class="btn-sm space" onclick={e => switchCliConfigRegion(e, region)}>
                            Failover {region}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Primary SSO session (default) */}
                    <pre class="add-line-numbers sso-session-block sso-session-primary">
                      <span>[sso-session default]</span>
                      <span>sso_start_url = {ssoStartUrls[data.master_account]}</span>
                      <span>sso_region = {CONF.awsLoginDetails.loginRegion}</span>
                      <span>sso_registration_scopes = sso:account:access</span>
                    </pre>

                    {hasFailover &&
                      Object.values(SsoFailoverRegion).map(region => (
                        <pre class={`add-line-numbers sso-session-block sso-session-${region}`} style="display:none">
                          <span>[sso-session failover-{region}]</span>
                          <span>sso_start_url = {getSsoFailoverUrl(data.master_account, region)}</span>
                          <span>sso_region = {region}</span>
                          <span>sso_registration_scopes = sso:account:access</span>
                        </pre>
                      ))}
                    <p></p>
                    <p>
                      Use the command <code>aws configure sso</code> to create the SSO config and enter the above values
                      when prompted. Ensure the region matches the SSO start URL. You need to authenticate during the
                      signin via a browser window.
                    </p>
                    <p>
                      Once authenticated, you can select the account and permission sets you have access to. A profile
                      is automatically created for each selected permission set like so:
                    </p>
                    {/* Primary profile (default) */}
                    <pre class="add-line-numbers cli-profile-block cli-profile-primary">
                      {cliConfigEnabled &&
                        data.idc_user_roles &&
                        data.idc_user_roles.map(role => (
                          <>
                            <span>
                              [profile {role}-{data.account_id}]{' '}
                            </span>
                            <span>sso_session = default</span>
                            <span>sso_account_id = {data.account_id}</span>
                            <span>
                              sso_role_name = {defaultIDCRoles.includes(role) ? role : `${data.account_id}_${role}`}
                            </span>
                            <span>region = {CONF.awsLoginDetails.loginRegion}</span>
                          </>
                        ))}
                    </pre>

                    {hasFailover &&
                      Object.values(SsoFailoverRegion).map(region => (
                        <pre class={`add-line-numbers cli-profile-block cli-profile-${region}`} style="display:none">
                          {cliConfigEnabled &&
                            data.idc_user_roles &&
                            data.idc_user_roles.map(role => (
                              <>
                                <span>
                                  [profile {role}-{data.account_id}]{' '}
                                </span>
                                <span>sso_session = failover-{region}</span>
                                <span>sso_account_id = {data.account_id}</span>
                                <span>
                                  sso_role_name = {defaultIDCRoles.includes(role) ? role : `${data.account_id}_${role}`}
                                </span>
                                <span>region = {region}</span>
                              </>
                            ))}
                        </pre>
                      ))}
                    <p></p>
                    <p>
                      In order to re-sign in to AWS, run <code>aws sso login --sso-session default</code>
                      {hasFailover && (
                        <>
                          {' '}
                          (or <code>--sso-session failover-eu-west-1</code> / <code>failover-us-east-1</code> for
                          failover profiles)
                        </>
                      )}
                      . Afterwards all profiles stored in <code>~/.aws/config</code> based on that session are usable.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* CLOUD-35639: The content of the `Orders` tab
            Displays all orders that belong to this specific account.
            The container (accountOrdersContainerId) will be populated with the datatable when loadAccountOrdersTab() is called.
            Using lazy loading keeps the page lightweight - the Orders table only loads when the user clicks the Orders tab.
        */}
        <div class="tab-pane fade" id={'orders' + data.account_id} role="tabpanel">
          <div class="detailsContent">
            <div class="row" name="orders-container">
              <div class="col-lg-12">
                {/* This div will be replaced with <Datatable /> when loadAccountOrdersTab() is called */}
                <div id={accountOrdersContainerId}>
                  {initialOrderIds.length > 0 && (
                    <div class="d-none">
                      {initialOrderIds.map(orderId => (
                        <span>{orderId}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CLOUD-36101: The content of the `Config Exports` tab
            Container is populated with real API data when loadConfigExportsTab() is called on first tab click.
        */}
        <div class="tab-pane fade" id={'config_exports' + data.account_id} role="tabpanel">
          <div class="detailsContent">
            <div class="row">
              <div class="col-lg-12">
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <h4 class="mb-0">AWS Config Data Exports</h4>
                  <button
                    class="btn-sm space"
                    title="Refresh export history"
                    onclick={() => loadConfigExportsTab(true)}>
                    <i class="fas fa-sync-alt"></i> Refresh
                  </button>
                </div>
                <p>
                  View past export requests for AWS Config snapshot data. Click the download button to get a pre-signed
                  S3 URL (valid for 1 hour) for the exported CSV file.
                </p>
                {/* This div is populated with the exports table when loadConfigExportsTab() is called */}
                <div id={configExportsContainerId}></div>
              </div>
            </div>
          </div>
        </div>

        {/* CLOUD-37764: The content of the `M2M Client Mappings` tab. The container is populated by the mappings UI. */}
        {m2mClientMappingsEnabled && (
          <div
            class="tab-pane fade d-none feature-flag-api-keys-m2m-client-account-mapping"
            id={'m2m_client_mappings' + data.account_id}
            role="tabpanel">
            <div class="detailsContent">
              <div class="row">
                <div class="col-lg-12">
                  <div class="d-flex align-items-center justify-content-between mb-2">
                    <h4 class="mb-0">M2M Client Mappings</h4>
                    <div>
                      <button
                        class="btn-sm space"
                        id={'add-m2m-client-mappings-' + data.account_id}
                        title="Add M2M client mappings"
                        onclick={() =>
                          openAddM2MClientMappingsModal(data.account_id, {
                            onSuccess: () => loadM2MClientMappingsTab(true),
                          })
                        }>
                        <i class="fas fa-plus"></i> Add
                      </button>
                      <button
                        class="btn-sm space"
                        id={'refresh-m2m-client-mappings-' + data.account_id}
                        title="Refresh M2M client mappings"
                        onclick={() => loadM2MClientMappingsTab(true)}>
                        <i class="fas fa-sync-alt"></i> Refresh
                      </button>
                    </div>
                  </div>
                  <p>M2M clients mapped to this AWS account.</p>
                  <div id={m2mClientMappingsContainerId}></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* The content of the `Organizations and Policies` tab */}
        <div class="tab-pane fade" id={'org' + data.account_id} role="tabpanel">
          <div class="row" name="org-details-container">
            {/* Content on the upper left corner, a table with some Org related information */}
            <div class="col-lg-12 col-xl-6">
              <div class="detailsContent">
                <h4>AWS Organizations</h4>
                <p>Find some information about the AWS account within the Organization</p>
                <div>
                  <InfoTable
                    data={data}
                    rowInfos={[
                      { Name: 'Organizations Master Account', Value: 'master_account' },
                      { Name: 'Organizations Id', Value: 'org_id' },
                      { Name: 'Organizations Name', Value: 'org_name' },
                      { Name: 'Organizations Unit Id', Value: 'org_ou_id' },
                      { Name: 'Organizations Unit Name', Value: 'org_ou_name' },
                      { Name: 'Organizations Account Status', Value: 'org_status' },
                      { Name: 'Last Org Update (UTC)', Value: 'org_status_update' },
                    ]}
                  />
                </div>
              </div>
            </div>

            {/* Content on the upper right corner, the OU structure which shows where the account is located */}
            <div class="col-lg-12 col-xl-6" name="org-container">
              <div class="detailsContent">
                <h4>OU Structure</h4>
                <p>
                  Here you can see where your AWS account is placed in the current OU structure. Visit the{' '}
                  <a
                    href="https://developer.bmwgroup.net/docs/public-cloud-platform-aws/1_beforeyoustart/platformdoc/overview_organization/organization/"
                    target="_blank"
                    rel="noopener noreferrer">
                    Developer Portal
                  </a>
                  <i class="px-1 fas fa-external-link-alt" /> for more details.
                </p>
                <p>
                  <pre>{ouTree}</pre>
                </p>
                <p>
                  Last Update of the OU info for this account: <strong>{data.org_status_update} UTC</strong>.
                </p>
              </div>
            </div>

            {/* Content at the bottom of the tab, buttons to load/disable/enable policies and accordions to show the SCPs and RCPs */}
            {policiesEnabled && (
              <div class="col-lg-12 col-xl-12" name="policies-container">
                <div class="col">
                  <div class="detailsContent">
                    <h4>
                      Policies <small>(SCPs and RCPs)</small>
                    </h4>
                    <p>
                      Please click the button "Load Policies" to load the currently assigned Service Control Policies
                      and Resource Control Policies for your AWS account.
                    </p>
                    <div class="btn-group detail-actions-btn-group">
                      <button
                        type="button"
                        class="btn btn-sm"
                        id="load-policies-button"
                        onclick={() => listPoliciesHandler('load-policies-button', data.account_id, sortedOrgUnits)}
                        title="Load all policies that are currently associated with this AWS account">
                        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        <span class="fas fa-search" aria-hidden="true"></span> Load Policies
                      </button>
                      {checkValueAgainstList(
                        'manage_accounts',
                        localStorage.permissions ? localStorage.permissions : false,
                      ) && (
                        <>
                          <button
                            type="button"
                            class="btn btn-sm"
                            onclick={event => temporarilyUnlockRootOrder(event)}
                            title="Temporary unlock the AWS root user for support tasks">
                            <span class="fas fa-unlock" aria-hidden="true">
                              {' '}
                            </span>{' '}
                            Temporarily Unlock Root for{' '}
                            <input
                              name="hours"
                              type="number"
                              size="2"
                              min="1"
                              max="12"
                              value="1"
                              onclick={event => event.stopPropagation()}
                              onkeyup={event => temporarilyUnlockRootHoursChange(event)}
                            />{' '}
                            hour(s)
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div class="col" id={'account_details_policies_' + data.account_id}></div>
              </div>
            )}
          </div>
        </div>

        <div class="tab-pane fade" id={'access' + data.account_id} role="tabpanel">
          <div class="col-lg-12 col-xl-12" name="temp-access-container">
            <div class="detailsContent">
              <h4>Temporary Account Access</h4>
              <p>
                Here you can request temporary access for <strong>maximum 1h</strong> for AWS account{' '}
                <strong>{data.account_id}</strong>. You'll get a link which opens a new browser tab with the target AWS
                session. You can't be logged in at AWS in the same browser session twice, so please log out from any
                other AWS session in your browser before you can use this link. All temporary access requests are logged
                in order to have a auditability.
              </p>
              <div name={'role_details_' + data.account_id}>
                <table class="table table-hover dataTable row-border compressed">
                  <thead>
                    <th class="table table-hover dataTable row-border nowrap compressed">Role</th>
                    <th class="table table-hover dataTable row-border nowrap compressed">Action</th>
                    <th class="table table-hover dataTable row-border nowrap compressed">Link</th>
                  </thead>
                  <tbody>
                    {tempAccessRoles.map(role => (
                      <tr role="row" class="compressed">
                        <td class="name small-width">
                          {role}
                          {copyButtonJSXHelper()}
                        </td>
                        <td class="value small-width">
                          <button
                            class="btn-sm space text-nowrap"
                            onclick={event => showTempRoleModal(event.target, role)}>
                            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            <span class="fas fa-user"></span> Get Temporary Access
                          </button>
                        </td>
                        <td class="value temp-link-column">
                          <span id={'role_link_value_' + role.split('/').pop()}>***</span>
                          {copyURLJSXHelper()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Only the fields newly read by the IDC Permission Set opt-out buttons are validated here; the rest of
// `data`'s large, pre-existing shape predates PropTypes usage in this component.
AccountTabs.propTypes = {
  data: PropTypes.shape({
    account_id: PropTypes.string,
    idc_ps_opted_out: PropTypes.bool,
  }),
  aamLoginDetails: PropTypes.shape({
    enabled: PropTypes.bool,
    login_url: PropTypes.string,
    roles: PropTypes.arrayOf(
      PropTypes.shape({
        role_name: PropTypes.string,
        iam_role_name: PropTypes.string,
        login_url: PropTypes.string,
      }),
    ),
  }),
  aamLoading: PropTypes.bool,
};
